import React, {FunctionComponent, useEffect, useState} from "react";
import {RootState} from "../../../../Config/Store";
import {useAppDispatch, useAppSelector} from "../../../../Config/Hooks";
import {ComparisonSkid, getEntities} from "./SkidComparisonChartReducer";
import {format} from 'date-fns';
import {truncateNumber} from "../../../../Utils/NumberUtil";
import {
    AChartX,
    Period,
    Skeleton,
    XAxis,
    YAxis, PeriodSelectorGroupProps
} from "@atiautomacao/ati-ui-library"
import {checkDiffDays} from "../../../../Utils/DataUitils";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import useInterval from "../../../../Shared/Hooks/useInterval";
import {useTheme} from "@mui/material";
import { getChartXOptions } from "../../../../Utils/ChartXOption";


interface SeriesOption {
    name: string;
    type: string;
    unity: string;
    yAxisIndex?: number | undefined;
    data: any[];
    date?: any;
    tooltip?: any;
}

interface SkidComparisonChartProps {
    period?: Period;
    periodIntervalGroup? : PeriodSelectorGroupProps;
    powerPlant: any;
}

const isAvailableEnergyValue = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);

const SkidComparisonChart: FunctionComponent<SkidComparisonChartProps> = (props: SkidComparisonChartProps) => {
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const comparisonSkid: Array<ComparisonSkid> = useAppSelector((state: RootState) => state.skidComparisonChart.entities);
    const normalizedComparisonSkid = Array.isArray(comparisonSkid) ? comparisonSkid : [];
    const { loading } = useAppSelector((state: RootState) => state.skidComparisonChart);
    const dispatch = useAppDispatch();
    const [showGraph, setShowGraph] = useState(false);
    const theme = useTheme();
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    // Setting range start period
    const initialPeriod: Period = {
        groupBy: "day",
        fromDateTime: new Date(),
        toDateTime: new Date()
    };

    const max = (values: Array<unknown>) => {
        if (!Array.isArray(values)) return 0;
        const numeric = values.filter(isAvailableEnergyValue);
        if (numeric.length === 0) return 0;
        return truncateNumber(Math.max(...numeric) * 1.15) ?? 0;
    }

    const valueFormatter = (value: unknown,fromDateTime:any, toDateTime:any) => {
        if (!isAvailableEnergyValue(value)) return "-";
        let data = formatCaption(fromDateTime, toDateTime)
        return  `${value} MWh ${data}`;
    }

    const [period, setPeriod] = useState<Period>(props.period ? props.period : initialPeriod);
    const effectivePeriod = props.period ?? period;
    const [series, setSeries] = useState<Array<SeriesOption>>(new Array<SeriesOption>);
    const [xAxis, setXAxis] = useState<XAxis>();
    const [yAxis, setYAxis] = useState<Array<YAxis>>(new Array<YAxis>);

    function formatCaption(fromDateTime: Date, toDateTime: Date): string{

        let dateInterval;
        if (period.groupBy && (period.groupBy.valueOf() === "none" || period.groupBy.valueOf() === "week" || period.groupBy.valueOf() === "month")) {
            dateInterval = format(fromDateTime, 'dd/MM') + ' a ' + format(toDateTime, 'dd/MM')
        } else {
            dateInterval = format(fromDateTime, 'HH:mm' ) + ' a ' + format(toDateTime, 'HH:mm')
        }

        return dateInterval
    }

    const newNameGap = () => {
        if (period.groupBy) {
            switch (period.groupBy) {
                case "day":
                    return 40;
                case "week":
                    return 46;
                case "month":
                    return 50;
                default:
                    return 40;
            }
        }
        return 40;
    };


    function dateByPeriod(): string{

        let fromDateTime = new Date();
        let toDateTime = new Date();
        let captionData :  string ;

        if(period.fromDateTime) {
            fromDateTime = period.fromDateTime
        }
        if(period.toDateTime) {
            toDateTime = period.toDateTime
        }

        captionData = (formatCaption(fromDateTime, toDateTime))
        return captionData
    }

    function findSkidComparisonByPeriod() {
        let datePeriod: any = dateByPeriod()
        const hasAvailableEnergy = normalizedComparisonSkid.some(item =>
            isAvailableEnergyValue(item.value) ||
            isAvailableEnergyValue(item.valueExpectedEnergy) ||
            isAvailableEnergyValue(item.valueExpectedEnergyCorrectedByIrradiation)
        );

        setShowGraph(hasAvailableEnergy)
        setXAxis({data: normalizedComparisonSkid.map(item => item.skidName)});

        setSeries([
                {
                    name: 'Energia Gerada',
                    data: normalizedComparisonSkid.map(item => isAvailableEnergyValue(item.value) ? truncateNumber(item.value, 2) : null),
                    type: 'bar',
                    unity: 'MWh',
                    date: datePeriod,
                    tooltip:{
                        valueFormatter: (value: any) => valueFormatter(value, period.fromDateTime, period.toDateTime)
                    }
                },
                {
                    name: 'Energia Esperada',
                    data: normalizedComparisonSkid.map(item => isAvailableEnergyValue(item.valueExpectedEnergy) ? truncateNumber(item.valueExpectedEnergy, 2) : null),
                    type: 'bar',
                    unity: 'MWh',
                    date: datePeriod,
                    tooltip:{
                        valueFormatter: (value: any) => valueFormatter(value, period.fromDateTime, period.toDateTime)
                    }
                },
                {
                    name: 'Energia Esperada Corrigida',
                    data: normalizedComparisonSkid.map(item => isAvailableEnergyValue(item.valueExpectedEnergyCorrectedByIrradiation) ? truncateNumber(item.valueExpectedEnergyCorrectedByIrradiation, 2) : null),
                    type: 'bar',
                    unity: 'MWh',
                    date: datePeriod,
                    tooltip:{
                        valueFormatter: (value: any) => valueFormatter(value, period.fromDateTime, period.toDateTime)
                    }
                }
            ]
        );

        const maxAxisY = max(normalizedComparisonSkid.map(item => item.value));
        const maxAxisYExpectedEnergy = max(normalizedComparisonSkid.map(item => item.valueExpectedEnergy));
        const maxAxisYExpectedEnergyCorrectedByIrradiation = max(normalizedComparisonSkid.map(item => item.valueExpectedEnergyCorrectedByIrradiation));

        const globalMax = Math.max(
            maxAxisY,
            maxAxisYExpectedEnergy,
            maxAxisYExpectedEnergyCorrectedByIrradiation
        );

        setYAxis([
            {
                name: 'MWh',
                max: globalMax > 1 ? globalMax : 1,
                min: 0,
                interval: globalMax > 1 ? truncateNumber(globalMax / 6) : 0.20,
                nameGap: newNameGap(),
                nameLocation: "middle"
            }
        ])
    }

    useEffect(() => {
        if(props.period){
            setPeriod(props.period);
            setSeries([])
            setXAxis(undefined)
            setYAxis([])
            setShowGraph(false)
        }
    }, [props.period]);

    useEffect(() => {
        setSeries([])
        setXAxis(undefined)
        setYAxis([])
        setShowGraph(false)
    }, [props.powerPlant]);


    const fetchData = () => {
        if (checkDiffDays(effectivePeriod.toDateTime, effectivePeriod.fromDateTime) > 0) {
            dispatch(getEntities({ period: effectivePeriod, idPowerPlant: props.powerPlant.id }));
        }
    };

    useEffect(() => {
        fetchData();
    }, [effectivePeriod, dispatch, props.powerPlant]);

    useInterval(() => {
        fetchData();
    }, userConfig.mainUpdateInterval); // 1 minute

    useEffect(() => {
        dateByPeriod();
        findSkidComparisonByPeriod();
    }, [comparisonSkid]);

    // @ts-ignore
    const option: AChartXProps['option'] = {
        ...getChartXOptions({name:"energia-gerada",date:new Date(new Date())}),
        color: ['#5470C6', '#91CC75', '#f1f41e', 'yellow'],
        legend: {
            type: 'scroll',
            show: true,
            orient: 'horizontal', // Alterado para orientação horizontal
            bottom: 2, // Posiciona a legenda na parte inferior
            left: 'center', // Centraliza a legenda horizontalmente
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            textStyle: {
                opacity: 1
            },
            itemStyle: {
                opacity: 1
            },
            data: ['Energia Gerada', 'Energia Esperada', 'Energia Esperada Corrigida']
        },
        xAxis: xAxis,
        yAxis: yAxis,
        series: series
    };

    return (
        <>
            {
                loading ?
                    <Skeleton animation="wave" height={350} variant="rounded" width={"100%"}/>
                    :
                    series && xAxis && yAxis && showGraph ?
                        <AChartX
                            key={`chart-power-energy-skid-comparison-${openSubMenu}`}
                            option={option}
                            height={350}
                            width={"100%"}
                            loading={false}
                            theme={theme.palette.mode}
                        />
                        :
                        <DataNotFound boxStyle={{height: 350, width: '100%'}}/>
            }
        </>
    );
}
export default SkidComparisonChart;
